import React, { useState, useEffect } from 'react';
import { promptService } from '../services/promptService';

const XNotificationList = ({ 
  notifications, 
  loading, 
  onEdit, 
  onDelete 
}) => {
  const [promptsMap, setPromptsMap] = useState({});
  const [promptsLoading, setPromptsLoading] = useState(true);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setPromptsLoading(true);
      const data = await promptService.getPrompts();
      // Create a map of prompt_id -> name for quick lookup
      const map = {};
      (data.prompts || []).forEach(prompt => {
        if (prompt.app_id && prompt.name) {
          map[prompt.app_id] = prompt.name;
        }
      });
      setPromptsMap(map);
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      setPromptsLoading(false);
    }
  };

  const getPromptName = (promptIdentifier) => {
    if (!promptIdentifier) return 'N/A';
    return promptsMap[promptIdentifier] || promptIdentifier;
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        No notifications configured. Click "Create New Notification" to get started.
      </div>
    );
  }

  const formatSchedule = (schedule) => {
    if (!schedule) return 'N/A';
    
    const { cadence_value, cadence_unit, start_time, end_time, all_day, run_once_daily, days_of_week } = schedule;
    
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const daysSuffix = days_of_week && days_of_week.length > 0 && days_of_week.length < 7
      ? ` on ${days_of_week.map(d => dayNames[d]).join(', ')}`
      : '';

    if (run_once_daily) {
      return `Once daily at ${start_time}${daysSuffix}`;
    }

    let scheduleStr = `Every ${cadence_value} ${cadence_value === 1 ? cadence_unit.slice(0, -1) : cadence_unit}`;
    
    if (all_day) {
      scheduleStr += ' (24/7)';
    } else {
      scheduleStr += ` (${start_time} - ${end_time})`;
    }

    scheduleStr += daysSuffix;
    
    return scheduleStr;
  };

  const formatLastRun = (lastRunAt) => {
    if (!lastRunAt) return 'Never';
    try {
      const date = new Date(lastRunAt);
      return date.toLocaleString();
    } catch (e) {
      return 'Invalid date';
    }
  };

  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle">
        <thead className="table-light">
          <tr>
            <th>Name</th>
            <th>Searches</th>
            <th>Prompt</th>
            <th>Schedule</th>
            <th>Last Run</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {notifications.map((notification) => (
            <tr key={notification.id}>
              <td>
                <div className="fw-bold">{notification.name}</div>
                {notification.description && (
                  <small className="text-muted">{notification.description}</small>
                )}
              </td>
              <td>
                <span className="badge bg-info">
                  {notification.search_config_ids?.length || 0} search(es)
                </span>
              </td>
              <td>
                {promptsLoading ? (
                  <span className="text-muted">Loading...</span>
                ) : (
                  <span className="badge bg-secondary">{getPromptName(notification.prompt_name)}</span>
                )}
              </td>
              <td>
                <small className="text-muted">
                  {formatSchedule(notification.schedule)}
                </small>
              </td>
              <td>
                <small className="text-muted">
                  {formatLastRun(notification.last_run_at)}
                </small>
              </td>
              <td>
                {notification.is_active ? (
                  <span className="badge bg-success">Active</span>
                ) : (
                  <span className="badge bg-secondary">Inactive</span>
                )}
              </td>
              <td>
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => onEdit(notification)}
                    title="Edit notification"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => onDelete(notification.id)}
                    title="Delete notification"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default XNotificationList;
